import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import supabase from '../utils/supabase-client';
import axiosInstance from '../utils/axios-auth';
const initialState = {
    user: null,
    session: null,
    isAuthenticated: false,
    loading: 'idle',
    error: null,
    needs_confirmation: false,
    email_confirmed: false,
    email_confirmation_sent: false,
};
// Simplified login thunk (no profile completion checks)
export const login = createAsyncThunk('auth/login', async (credentials, { rejectWithValue }) => {
    try {
        // Store rememberMe preference for token refresh logic
        if (credentials.rememberMe) {
            localStorage.setItem('rememberMe', 'true');
        }
        else {
            localStorage.removeItem('rememberMe');
        }
        const { data, error } = await supabase.auth.signInWithPassword({
            email: credentials.email,
            password: credentials.password
        });
        if (error)
            throw error;
        if (!data.user || !data.session) {
            return rejectWithValue({ message: "Login failed - invalid credentials" });
        }
        // Get user role from metadata
        const userRole = data.user.user_metadata.role || 'student';
        // Return simplified user object
        return {
            user: {
                id: data.user.id,
                email: data.user.email || '',
                first_name: data.user.user_metadata.first_name || '',
                last_name: data.user.user_metadata.last_name || '',
                role: userRole
            },
            session: {
                access_token: data.session.access_token,
                refresh_token: data.session.refresh_token
            }
        };
    }
    catch (error) {
        console.error('Login error:', error);
        // Handle rate limiting response
        if (error.response?.status === 429) {
            return rejectWithValue({
                message: error.response.data.message || 'Account temporarily locked',
                attemptsRemaining: error.response.data.attemptsRemaining || 0,
                isLocked: error.response.data.isLocked || false,
                lockoutExpires: error.response.data.lockoutExpires
            });
        }
        // Handle regular login errors with rate limiting info
        if (error.response?.data?.attemptsRemaining !== undefined) {
            return rejectWithValue({
                message: error.response.data.message || 'Invalid email or password',
                attemptsRemaining: error.response.data.attemptsRemaining,
                isLocked: error.response.data.isLocked || false
            });
        }
        return rejectWithValue({
            message: error.message || 'Invalid email or password'
        });
    }
});
// Simplified signup function
const handleSignup = async (signupData, role, rejectWithValue) => {
    const { email, password, first_name, last_name, date_of_birth, parent_email, parent_guardian_name } = signupData;
    try {
        // Add pre-validation for signup data
        if (!email || !password) {
            console.error('handleSignup: Missing required fields - email or password');
            return rejectWithValue({ message: 'Email and password are required' });
        }
        // NOTE: Terms acceptance is no longer collected at signup. It is captured
        // after sign-in via the blocking TermsAndConditionsPopup in the onboarding
        // flow — a single gate that covers both email and Google (OAuth) sign-ups.
        // Create user with Supabase Auth
        // NOTE: This frontend signup directly calls Supabase Auth, which triggers database triggers
        // that automatically call the post-signup hook (server/hooks/post_signup_hook.js).
        // The post-signup hook sends confirmation emails via our custom email service (Zoho SMTP).
        // 
        // Email Flow:
        // 1. User signs up via this frontend call
        // 2. Supabase creates the user and triggers database triggers
        // 3. Database triggers call postSignupHook.handlePostSignup()
        // 4. Post-signup hook ensures email service is initialized
        // 5. Post-signup hook generates confirmation URL and sends email via custom email service
        //
        // Include emailRedirectTo so Supabase can generate a valid confirmation link
        // This is critical for email confirmation to work properly
        // Get redirect URL - must match EXACTLY what is in Supabase > Auth > URL Configuration > Redirect URLs
        // Lessons learned: Use window.location.origin to support both www and non-www
        // This prevents cross-subdomain redirects (e.g. non-www to www) which can strip the URL hash containing tokens
        const emailRedirectTo = `${window.location.origin}/auth/callback`;
        const studentComplianceMeta = role === 'student' &&
            typeof date_of_birth === 'string' &&
            date_of_birth.trim() !== ''
            ? {
                signup_date_of_birth: date_of_birth.trim(),
                ...(typeof parent_email === 'string' && parent_email.trim() !== ''
                    ? { signup_parent_email: parent_email.trim() }
                    : {}),
                ...(typeof parent_guardian_name === 'string' && parent_guardian_name.trim() !== ''
                    ? { signup_parent_guardian_name: parent_guardian_name.trim() }
                    : {})
            }
            : {};
        const { data: authData, error: authError } = await supabase.auth.signUp({
            email,
            password,
            options: {
                emailRedirectTo: emailRedirectTo,
                data: {
                    role: role,
                    first_name: first_name || 'New',
                    last_name: last_name || (role === 'student' ? 'Student' : 'Tutor'),
                    // terms_accepted is intentionally NOT set here — terms are accepted
                    // after sign-in via the onboarding TermsAndConditionsPopup.
                    ...studentComplianceMeta
                },
            },
        });
        if (authError) {
            console.error(`handleSignup: Auth error during signup:`, authError);
            throw authError;
        }
        if (!authData.user) {
            console.error(`handleSignup: No user data returned from auth signup`);
            throw new Error('Signup failed - user data missing');
        }
        // Profile is automatically created by the database trigger
        // Onboarding record is also automatically created by database trigger
        // No need for manual profile/onboarding creation here
        // Terms are NOT accepted here. terms_conditions_completed stays false so the
        // blocking TermsAndConditionsPopup is shown on the onboarding screen after
        // sign-in (the single terms gate for both email and Google sign-ups).
        if (role === 'student' && authData.session?.access_token && authData.user) {
            try {
                const { syncPendingStudentSignupComplianceFromAuthUser } = await import('../utils/sync-pending-student-signup-compliance');
                await syncPendingStudentSignupComplianceFromAuthUser(authData.user);
            }
            catch (syncErr) {
                const msg = syncErr instanceof Error ? syncErr.message : String(syncErr);
                console.warn('[handleSignup] Student compliance sync failed (AuthCallback may retry):', msg);
            }
        }
        // Verify onboarding record was created (should be automatic via trigger)
        try {
            const onboardingTable = role === 'student' ? 'student_onboarding_simple' : 'tutor_onboarding';
            const { data: _onboardingData, error: onboardingCheckError } = await supabase
                .from(onboardingTable)
                .select('id')
                .eq(role === 'student' ? 'student_id' : 'tutor_id', authData.user.id)
                .single();
            if (onboardingCheckError && onboardingCheckError.code !== 'PGRST116') {
                // PGRST116 = no rows returned, which means trigger didn't fire
                console.warn('Onboarding record not found after signup. Trigger may not have fired:', onboardingCheckError);
                // Try to create it manually as fallback
                try {
                    await supabase
                        .from(onboardingTable)
                        .insert({
                        [role === 'student' ? 'student_id' : 'tutor_id']: authData.user.id,
                        current_step: 'email_confirmation'
                    });
                }
                catch (fallbackError) {
                    console.error('Failed to create onboarding record as fallback:', fallbackError);
                    // Still don't fail signup, but log the error
                }
            }
        }
        catch (verifyError) {
            console.warn('Error verifying onboarding record creation:', verifyError);
            // Don't fail signup, but log the warning
        }
        const user = {
            id: authData.user.id,
            email: authData.user.email ?? email,
            role: role,
            first_name: first_name || 'New',
            last_name: last_name || (role === 'student' ? 'Student' : 'Tutor')
        };
        const session = {
            access_token: authData.session?.access_token ?? null,
            refresh_token: authData.session?.refresh_token ?? null,
        };
        // Check if email confirmation is required
        // If user exists but no session, email confirmation is needed
        const needs_confirmation = authData.user && !authData.session;
        return { user, session, needs_confirmation };
    }
    catch (error) {
        console.error('Signup error catch block:', error);
        let message = `Signup failed for ${role}.`;
        // Standardized error message mapping
        if (error?.message?.includes('User already registered') || error?.message?.includes('already been registered')) {
            message = 'An account with this email already exists.';
        }
        else if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
            message = 'Network error. Please check your connection and try again.';
        }
        else if (error?.message?.includes('timeout')) {
            message = 'Request timed out. Please try again.';
        }
        else if (error?.message) {
            message = error.message;
        }
        else if (typeof error === 'string') {
            message = error;
        }
        else {
            message = `An unexpected error occurred during ${role} signup. Please try again later.`;
        }
        return rejectWithValue({ message });
    }
};
// Signup Student Thunk (Simplified)
export const signupStudent = createAsyncThunk('auth/signupStudent', (signupData, { rejectWithValue }) => handleSignup(signupData, 'student', rejectWithValue));
// Signup Tutor Thunk (Simplified)
export const signupTutor = createAsyncThunk('auth/signupTutor', (signupData, { rejectWithValue }) => handleSignup(signupData, 'tutor', rejectWithValue));
// Enhanced Tutor Signup with Background Check
export const signupTutorWithVerification = createAsyncThunk('auth/signupTutorWithVerification', async (signupData, { rejectWithValue }) => {
    try {
        // First, create the auth user using the existing handleSignup function
        const authResult = await handleSignup(signupData, 'tutor', rejectWithValue);
        // If auth signup failed, return the error
        if (!authResult.user) {
            return rejectWithValue({ message: 'Failed to create user account' });
        }
        // Note: Background check creation is now deferred until after address step
        // This is handled in SignupForm.handleAddressStepComplete() after country is available
        // We no longer create background checks here during initial signup
        return authResult;
    }
    catch (error) {
        console.error('Error in signupTutorWithVerification:', error);
        return rejectWithValue({ message: error.message || 'Tutor signup with verification failed' });
    }
});
// Set User action creator
export const setUser = createAsyncThunk('auth/setUser', async (payload, { rejectWithValue }) => {
    try {
        return payload;
    }
    catch (error) {
        return rejectWithValue(error.message || 'Failed to set user');
    }
});
// Logout User thunk
export const logoutUser = createAsyncThunk('auth/logoutUser', async (_, { rejectWithValue }) => {
    try {
        // Clear CSRF token and session caches
        const { clearCSRFTokenCache, clearSessionCache } = await import('../utils/axios-auth');
        clearCSRFTokenCache();
        clearSessionCache();
        // Clear localStorage first
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('supabase_auth_token');
        localStorage.removeItem('supabase_auth_options');
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('persist:root');
        // Purge redux-persist so the next login does not reuse stale user id/role
        try {
            const { persistor } = await import('./store');
            await persistor.purge();
        }
        catch (purgeError) {
            console.warn('Redux persist purge on logout:', purgeError);
        }
        // Sign out from Supabase
        const { error } = await supabase.auth.signOut();
        if (error) {
            console.warn('Supabase signOut error:', error);
            // Don't throw error - still clear local state
        }
    }
    catch (error) {
        console.error('Logout error:', error);
        // Even if logout fails, we should clear local state
        try {
            const { clearCSRFTokenCache, clearSessionCache } = await import('../utils/axios-auth');
            clearCSRFTokenCache();
            clearSessionCache();
        }
        catch (importError) {
            // Ignore import errors during logout
        }
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('supabase_auth_token');
        localStorage.removeItem('supabase_auth_options');
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('persist:root');
        try {
            const { persistor } = await import('./store');
            await persistor.purge();
        }
        catch {
            // Ignore purge errors during error-path logout
        }
        return rejectWithValue(error.message || 'Failed to logout');
    }
});
// Email Confirmation Thunks
export const confirmEmail = createAsyncThunk('auth/confirmEmail', async ({ token }, { rejectWithValue }) => {
    try {
        const { data, error } = await supabase.auth.verifyOtp({
            token_hash: token,
            type: 'email'
        });
        if (error)
            throw error;
        if (!data.user || !data.session) {
            return rejectWithValue({ message: "Email confirmation failed" });
        }
        const userRole = data.user.user_metadata.role || 'student';
        return {
            user: {
                id: data.user.id,
                email: data.user.email || '',
                first_name: data.user.user_metadata.first_name || '',
                last_name: data.user.user_metadata.last_name || '',
                role: userRole
            },
            session: {
                access_token: data.session.access_token,
                refresh_token: data.session.refresh_token
            }
        };
    }
    catch (error) {
        console.error('Email confirmation error:', error);
        return rejectWithValue({
            message: error.message || 'Email confirmation failed'
        });
    }
});
/**
 * Verify email using a 6-digit OTP code (typed by the user) instead of a link.
 *
 * Why this exists:
 * - Confirmation links are frequently consumed before the user clicks them by email
 *   security scanners (Outlook SafeLinks, Mimecast, Proofpoint, corporate gateways)
 *   that GET the single-use URL to scan it, causing "invalid/expired link" errors.
 * - Links can also break on cross-subdomain (www <-> non-www) redirects that strip the
 *   token hash, and in in-app browsers / mobile deep-links.
 * A typed 6-digit code cannot be prefetched and works cross-device.
 *
 * Difference from `confirmEmail`:
 * - `confirmEmail` verifies a `token_hash` extracted from the confirmation URL.
 * - `verifyEmailOtp` verifies the plain 6-digit `token` the user typed, which requires
 *   the `email` as well. `type: 'email'` covers the "Confirm signup" template OTP.
 */
export const verifyEmailOtp = createAsyncThunk('auth/verifyEmailOtp', async ({ email, token }, { rejectWithValue }) => {
    try {
        const normalizedEmail = (email || '').trim().toLowerCase();
        const normalizedToken = (token || '').replace(/\D/g, '');
        if (!normalizedEmail) {
            return rejectWithValue({ message: 'Email address is required to verify the code.' });
        }
        if (normalizedToken.length !== 6) {
            return rejectWithValue({ message: 'Enter the 6-digit code from your email.' });
        }
        const { data, error } = await supabase.auth.verifyOtp({
            email: normalizedEmail,
            token: normalizedToken,
            type: 'email'
        });
        if (error)
            throw error;
        if (!data.user || !data.session) {
            return rejectWithValue({ message: 'Email confirmation failed. Please request a new code.' });
        }
        const userRole = data.user.user_metadata.role || 'student';
        return {
            user: {
                id: data.user.id,
                email: data.user.email || normalizedEmail,
                first_name: data.user.user_metadata.first_name || '',
                last_name: data.user.user_metadata.last_name || '',
                role: userRole
            },
            session: {
                access_token: data.session.access_token,
                refresh_token: data.session.refresh_token
            }
        };
    }
    catch (error) {
        console.error('Email OTP verification error:', error);
        const raw = (error?.message || '').toLowerCase();
        let message = error?.message || 'Email confirmation failed';
        if (raw.includes('expired')) {
            message = 'That code has expired. Please request a new one.';
        }
        else if (raw.includes('invalid') || raw.includes('token')) {
            message = 'That code is incorrect. Please check the 6 digits and try again.';
        }
        return rejectWithValue({ message });
    }
});
/**
 * Resend email confirmation using Supabase Auth directly
 *
 * This uses Supabase's built-in resend() method for better reliability.
 * Benefits:
 * - Avoids Cloudflare worker proxy issues (no 500 errors)
 * - No API URL construction (avoids undefined/api/... errors)
 * - Uses Supabase's built-in email service (configured in Dashboard)
 * - Simpler implementation with fewer points of failure
 * - Consistent with Supabase's email verification flow
 *
 * Migration Notes:
 * - Previously used backend endpoint `/api/auth/email-confirmation/resend`
 * - Backend endpoint is now deprecated (kept for backward compatibility)
 * - Migrated to client-side resend() to fix 500 errors in production
 *
 * Lessons Learned:
 * - Proper error handling with user-friendly messages
 * - Rate limiting awareness (Supabase enforces this)
 * - Type safety with proper error types
 */
export const resendEmailConfirmation = createAsyncThunk('auth/resendEmailConfirmation', async ({ email }, { rejectWithValue }) => {
    try {
        // Validate email input (lessons learned: always validate inputs)
        if (!email || typeof email !== 'string') {
            return rejectWithValue({
                message: 'Email address is required'
            });
        }
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail) {
            return rejectWithValue({
                message: 'Email address cannot be empty'
            });
        }
        // Basic email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(normalizedEmail)) {
            return rejectWithValue({
                message: 'Invalid email address format'
            });
        }
        // Use Supabase's built-in resend() method (client-side)
        // This avoids Cloudflare worker configuration issues and uses Supabase's email system
        // Note: Supabase will send the email using their SMTP service
        const { data, error } = await supabase.auth.resend({
            type: 'signup',
            email: normalizedEmail,
            options: {
                emailRedirectTo: `${window.location.origin}/auth/callback`
            }
        });
        // Temporary logging for debugging email delivery issues
        console.log('Resend response:', { success: !error, email: normalizedEmail, error });
        if (error) {
            // Handle specific error messages from Supabase
            let userMessage = error.message || 'Failed to resend email confirmation';
            if (userMessage.includes('rate limit') || userMessage.includes('too many')) {
                userMessage = 'Too many requests. Please wait a few minutes before trying again.';
            }
            else if (userMessage.includes('not found') || userMessage.includes('user')) {
                userMessage = 'No account found with this email address. Please check your email and try again.';
            }
            else if (userMessage.includes('already confirmed') || userMessage.includes('confirmed')) {
                userMessage = 'This email address is already confirmed. You can sign in now.';
            }
            console.error('Supabase resend error:', {
                error: userMessage,
                email: normalizedEmail.substring(0, 3) + '***' // Don't log full email
            });
            return rejectWithValue({ message: userMessage });
        }
        // Success - Email sent via Supabase's built-in email service
        return { success: true };
    }
    catch (error) {
        console.error('Unexpected error in resendEmailConfirmation:', error);
        // Lessons learned: Sanitize error messages before showing to user
        let errorMessage = 'Failed to resend email confirmation. Please try again later.';
        if (error?.response?.data?.error) {
            // Backend returned a structured error
            errorMessage = error.response.data.error;
        }
        else if (error instanceof Error) {
            const sanitized = error.message
                .replace(/undefined\/api\/[^\s]+/g, '')
                .replace(/Fetch API cannot load:[^\s]+/g, '')
                .replace(/at\s+[^\n]+/g, '')
                .trim();
            if (sanitized && !sanitized.includes('TypeError') && !sanitized.includes('undefined')) {
                errorMessage = sanitized;
            }
        }
        return rejectWithValue({ message: errorMessage });
    }
});
export const checkEmailConfirmationStatus = createAsyncThunk('auth/checkEmailConfirmationStatus', async ({ userId }, { rejectWithValue }) => {
    try {
        const { data, error } = await supabase
            .rpc('user_needs_email_confirmation', { p_user_id: userId });
        if (error)
            throw error;
        return {
            needsConfirmation: data || true,
            emailConfirmed: !data
        };
    }
    catch (error) {
        console.error('Check email confirmation status error:', error);
        return rejectWithValue({
            message: error.message || 'Failed to check email confirmation status'
        });
    }
});
// Simplified profile update thunks
export const updateStudentProfile = createAsyncThunk('auth/updateStudentProfile', async (profileData, { getState, rejectWithValue }) => {
    try {
        const state = getState();
        const userId = state.auth.user?.id;
        const userEmail = state.auth.user?.email;
        if (!userId) {
            throw new Error('User ID not found');
        }
        // Use RPC function instead of direct table operations
        const { data, error } = await supabase
            .rpc('upsert_student_profile', {
            p_id: userId,
            p_email: profileData.email || userEmail,
            p_first_name: profileData.first_name || null,
            p_last_name: profileData.last_name || null,
            p_date_of_birth: profileData.date_of_birth || null,
            p_grade_level: profileData.grade_level || null,
            p_subjects: profileData.subjects || null,
            p_parent_guardian_name: profileData.parent_guardian_name || null,
            p_parent_email: profileData.parent_email || null,
            p_display_photo: profileData.display_photo || null,
            p_settings: profileData.settings || null,
            p_address_id: profileData.address_id || null
        });
        if (error)
            throw error;
        if (!data)
            throw new Error('Failed to update profile - no data returned');
        return {
            ...state.auth.user,
            ...data
        };
    }
    catch (error) {
        console.error('Student profile update error:', error);
        return rejectWithValue(error.message || 'Failed to update student profile');
    }
});
export const updateTutorProfile = createAsyncThunk('auth/updateTutorProfile', async (profileData, { getState, rejectWithValue }) => {
    try {
        const state = getState();
        const userId = state.auth.user?.id;
        const userEmail = state.auth.user?.email;
        if (!userId) {
            throw new Error('User ID not found');
        }
        // Use RPC function instead of direct table operations
        const { data, error } = await supabase
            .rpc('upsert_tutor_profile', {
            p_id: userId,
            p_email: profileData.email || userEmail,
            p_first_name: profileData.first_name || null,
            p_last_name: profileData.last_name || null,
            p_date_of_birth: profileData.date_of_birth || null,
            p_subjects: profileData.subjects || null,
            p_bio: profileData.bio || null,
            p_hourly_rate: profileData.hourly_rate || null,
            p_display_photo: profileData.display_photo || null,
            p_settings: profileData.settings || null,
            p_address_id: profileData.address_id || null
        });
        if (error)
            throw error;
        if (!data)
            throw new Error('Failed to update profile - no data returned');
        return {
            ...state.auth.user,
            ...data
        };
    }
    catch (error) {
        console.error('Tutor profile update error:', error);
        return rejectWithValue(error.message || 'Failed to update tutor profile');
    }
});
/**
 * Fetch User Profile Thunk (Plan 4 Option A: single source of truth via API)
 * Uses GET /api/user/current so profile comes from the API only, not Supabase client.
 */
export const fetch_user_profile = createAsyncThunk('auth/fetchUserProfile', async ({ userId, userRole, userEmail }, { rejectWithValue }) => {
    if (!userId || !userRole || userId === 'pending_confirmation') {
        console.error(`fetch_user_profile rejected: Invalid args - userId: ${userId}, userRole: ${userRole}`);
        return rejectWithValue('User ID or role not found or invalid');
    }
    try {
        const response = await axiosInstance.get('/user/current');
        const profileData = response.data?.data;
        if (!response.data?.success || !profileData) {
            console.warn(`Profile not found for user ${userId} with role ${userRole}.`);
            return rejectWithValue('User profile not found');
        }
        // Return full profile from API (grade_level, subjects, address, etc.) so auth.user
        // has complete data for settings forms. Partial mapping caused form data to disappear.
        return {
            ...profileData,
            id: profileData.id ?? userId,
            email: profileData.email ?? userEmail,
            role: profileData.role ?? userRole,
        };
    }
    catch (error) {
        console.error('Fetch profile error (API):', error?.response?.data ?? error);
        // Reject instead of returning minimal user - preserves existing auth.user on failure
        return rejectWithValue(error?.response?.data?.message ?? error?.message ?? 'Failed to fetch profile');
    }
});
// Auth Slice Definition with Reducers
const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        resetLoadingState(state) {
            if (state.loading === 'pending') {
                state.loading = 'idle';
                if (!state.error) {
                    state.error = 'Login process timed out. Please try again.';
                }
            }
        },
        updateEmailConfirmationStatus: (state, action) => {
            state.email_confirmed = action.payload.emailConfirmed;
            state.needs_confirmation = !action.payload.emailConfirmed;
        },
    },
    extraReducers: (builder) => {
        builder
            // Login
            .addCase(login.fulfilled, (state, action) => {
            state.user = action.payload.user;
            state.session = action.payload.session;
            state.isAuthenticated = true;
            state.loading = 'succeeded';
            state.needs_confirmation = false;
            state.email_confirmed = true;
            state.error = null;
        })
            .addCase(login.pending, (state) => {
            state.loading = 'pending';
            state.error = null;
        })
            .addCase(login.rejected, (state, action) => {
            state.loading = 'failed';
            state.error = action.payload?.message || 'Login failed';
            if (action.payload?.needs_confirmation) {
                state.needs_confirmation = true;
                state.email_confirmed = false;
            }
        })
            // Signup Student
            .addCase(signupStudent.fulfilled, (state, action) => {
            state.user = action.payload.user;
            state.session = action.payload.session;
            state.isAuthenticated = action.payload.session !== null;
            state.loading = 'succeeded';
            state.needs_confirmation = action.payload.needs_confirmation || false;
            state.email_confirmed = !action.payload.needs_confirmation;
            state.email_confirmation_sent = action.payload.needs_confirmation || false;
        })
            .addCase(signupStudent.pending, (state) => {
            state.loading = 'pending';
            state.error = null;
        })
            .addCase(signupStudent.rejected, (state, action) => {
            state.loading = 'failed';
            state.error = action.payload?.message || 'Student signup failed';
        })
            // Signup Tutor
            .addCase(signupTutor.fulfilled, (state, action) => {
            state.user = action.payload.user;
            state.session = action.payload.session;
            state.isAuthenticated = action.payload.session !== null;
            state.loading = 'succeeded';
            state.needs_confirmation = action.payload.needs_confirmation || false;
            state.email_confirmed = !action.payload.needs_confirmation;
            state.email_confirmation_sent = action.payload.needs_confirmation || false;
        })
            .addCase(signupTutor.pending, (state) => {
            state.loading = 'pending';
            state.error = null;
        })
            .addCase(signupTutor.rejected, (state, action) => {
            state.loading = 'failed';
            state.error = action.payload?.message || 'Tutor signup failed';
        })
            // Signup Tutor with Verification
            .addCase(signupTutorWithVerification.fulfilled, (state, action) => {
            state.user = action.payload.user;
            state.session = action.payload.session;
            state.isAuthenticated = action.payload.session !== null;
            state.loading = 'succeeded';
            state.needs_confirmation = action.payload.needs_confirmation || false;
            state.email_confirmed = !action.payload.needs_confirmation;
            state.email_confirmation_sent = action.payload.needs_confirmation || false;
        })
            .addCase(signupTutorWithVerification.pending, (state) => {
            state.loading = 'pending';
            state.error = null;
        })
            .addCase(signupTutorWithVerification.rejected, (state, action) => {
            state.loading = 'failed';
            state.error = action.payload?.message || 'Tutor signup with verification failed';
        })
            // Logout user
            .addCase(logoutUser.fulfilled, (state) => {
            state.user = null;
            state.session = null;
            state.isAuthenticated = false;
            state.needs_confirmation = false;
            state.email_confirmed = false;
            state.email_confirmation_sent = false;
            state.error = null;
            state.loading = 'succeeded';
        })
            .addCase(logoutUser.pending, (state) => {
            state.loading = 'pending';
        })
            .addCase(logoutUser.rejected, (state, action) => {
            state.loading = 'failed';
            state.error = action.payload || null;
            // Still clear auth state even if backend logout fails
            state.user = null;
            state.session = null;
            state.isAuthenticated = false;
            state.needs_confirmation = false;
            state.email_confirmed = false;
            state.email_confirmation_sent = false;
        })
            // Email Confirmation
            .addCase(confirmEmail.fulfilled, (state, action) => {
            state.user = action.payload.user;
            state.session = action.payload.session;
            state.isAuthenticated = true;
            state.loading = 'succeeded';
            state.needs_confirmation = false;
            state.email_confirmed = true;
            state.email_confirmation_sent = false;
            state.error = null;
        })
            .addCase(confirmEmail.pending, (state) => {
            state.loading = 'pending';
            state.error = null;
        })
            .addCase(confirmEmail.rejected, (state, action) => {
            state.loading = 'failed';
            state.error = action.payload?.message || 'Email confirmation failed';
        })
            // Email Confirmation via 6-digit OTP code
            .addCase(verifyEmailOtp.fulfilled, (state, action) => {
            state.user = action.payload.user;
            state.session = action.payload.session;
            state.isAuthenticated = true;
            state.loading = 'succeeded';
            state.needs_confirmation = false;
            state.email_confirmed = true;
            state.email_confirmation_sent = false;
            state.error = null;
        })
            .addCase(verifyEmailOtp.pending, (state) => {
            state.loading = 'pending';
            state.error = null;
        })
            .addCase(verifyEmailOtp.rejected, (state, action) => {
            state.loading = 'failed';
            state.error = action.payload?.message || 'Email confirmation failed';
        })
            // Resend Email Confirmation
            .addCase(resendEmailConfirmation.fulfilled, (state) => {
            state.email_confirmation_sent = true;
            state.loading = 'succeeded';
            state.error = null;
        })
            .addCase(resendEmailConfirmation.pending, (state) => {
            state.loading = 'pending';
            state.error = null;
        })
            .addCase(resendEmailConfirmation.rejected, (state, action) => {
            state.loading = 'failed';
            state.error = action.payload?.message || 'Failed to resend email confirmation';
        })
            // Check Email Confirmation Status
            .addCase(checkEmailConfirmationStatus.fulfilled, (state, action) => {
            state.needs_confirmation = action.payload.needsConfirmation;
            state.email_confirmed = action.payload.emailConfirmed;
            state.loading = 'succeeded';
            state.error = null;
        })
            .addCase(checkEmailConfirmationStatus.pending, (state) => {
            state.loading = 'pending';
            state.error = null;
        })
            .addCase(checkEmailConfirmationStatus.rejected, (state, action) => {
            state.loading = 'failed';
            state.error = action.payload?.message || 'Failed to check email confirmation status';
        })
            // Set User
            .addCase(setUser.fulfilled, (state, action) => {
            if (action.payload) {
                state.user = action.payload.user;
                state.session = action.payload.session;
                state.isAuthenticated = !!action.payload.user;
            }
            state.loading = 'succeeded';
        })
            // Fetch User Profile
            .addCase(fetch_user_profile.fulfilled, (state, action) => {
            state.user = action.payload;
            state.loading = 'succeeded';
        })
            .addCase(fetch_user_profile.pending, (state) => {
            // Background refreshes (ProfileInitializer / PersistentDataProvider) must not
            // flip global loading — that unmounts ProtectedAdminRoute and similar gates.
            if (!state.isAuthenticated || !state.user) {
                state.loading = 'pending';
            }
        })
            .addCase(fetch_user_profile.rejected, (state, action) => {
            if (state.isAuthenticated && state.user) {
                state.error = action.payload || null;
            }
            else {
                state.loading = 'failed';
                state.error = action.payload || null;
            }
        })
            // Update Student Profile
            .addCase(updateStudentProfile.fulfilled, (state, action) => {
            state.user = action.payload;
            state.loading = 'succeeded';
        })
            .addCase(updateStudentProfile.pending, (state) => {
            state.loading = 'pending';
        })
            .addCase(updateStudentProfile.rejected, (state, action) => {
            state.loading = 'failed';
            state.error = action.payload || null;
        })
            // Update Tutor Profile
            .addCase(updateTutorProfile.fulfilled, (state, action) => {
            state.user = action.payload;
            state.loading = 'succeeded';
        })
            .addCase(updateTutorProfile.pending, (state) => {
            state.loading = 'pending';
        })
            .addCase(updateTutorProfile.rejected, (state, action) => {
            state.loading = 'failed';
            state.error = action.payload || null;
        });
    },
});
// Export actions
export const { clearError, resetLoadingState, updateEmailConfirmationStatus } = authSlice.actions;
// Export aliases for compatibility
export const loginUser = login;
// Create selectors for accessing auth state
export const selectCurrentUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectNeedsConfirmation = (state) => state.auth.needs_confirmation;
export const selectEmailConfirmed = (state) => state.auth.email_confirmed;
export const selectEmailConfirmationSent = (state) => state.auth.email_confirmation_sent;
export const selectCurrentUserId = (state) => state.auth.user?.id || null;
export const selectAuthToken = (state) => state.auth.session?.access_token || null;
export default authSlice.reducer;
